import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, Send, X, Paperclip, Sparkles, ChevronDown, Users, UserCircle, Mail, FileText, IdCard, Trash2 } from 'lucide-react';
import RichTextEditor from './RichTextEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Modal para redactar un correo NUEVO.
 * - Selector de cuenta remitente
 * - To/Cc/Bcc con chips + autocomplete matriculados
 * - Selector de grupos (envío masivo por BCC)
 * - Adjuntos (Emergent Object Storage)
 * - Firma automática (leída de la cuenta seleccionada)
 * - Respuestas frecuentes (canned)
 * - Autosave debounced a `mail_drafts` (visible en carpeta Borradores)
 */
export function ComposeDialog({ open, onClose, onSent, accounts = [], initialData = null }) {
  const [accountId, setAccountId] = useState('');
  const [firmaHtml, setFirmaHtml] = useState('');
  // Chips como objetos: { email, trabajador_id?, apellido?, nombre?, numero_matricula? }
  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [bcc, setBcc] = useState([]);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [cannedOpen, setCannedOpen] = useState(false);
  const [canned, setCanned] = useState([]);
  const [gruposOpen, setGruposOpen] = useState(false);
  const gruposDropdownRef = useRef(null);
  const [grupos, setGrupos] = useState([]);
  const [grupoSel, setGrupoSel] = useState(null);
  const [loadingGrupo, setLoadingGrupo] = useState(false);
  const fileInputRef = useRef(null);

  // Draft autosave
  const [draftId, setDraftId] = useState(null);
  const [draftStatus, setDraftStatus] = useState(''); // 'guardando' | 'guardado' | 'error' | ''
  const [hydrating, setHydrating] = useState(false);  // true durante prefill de draft existente
  const autosaveTimer = useRef(null);
  const userTouchedRef = useRef(false); // sólo autosave tras primera edición manual
  const draftLoadedRef = useRef(false); // true una vez que el prefill terminó

  // Preselección cuenta y carga inicial (draft > forward > compose vacío)
  useEffect(() => {
    if (!open) return;
    draftLoadedRef.current = false;
    userTouchedRef.current = false;
    setDraftStatus('');

    const loadInitial = async () => {
      // Traer canned + grupos en paralelo (no bloquea)
      (async () => {
        try {
          const [c, g] = await Promise.all([
            axios.get(`${API_URL}/bandeja-unificada/canned-responses`),
            axios.get(`${API_URL}/bandeja-unificada/grupos`),
          ]);
          setCanned(c.data || []);
          setGrupos(g.data || []);
        } catch (e) { /* silent */ }
      })();

      // ── Caso 1: Reabrir borrador existente por id ──
      if (initialData?.draft_id) {
        setHydrating(true);
        try {
          const r = await axios.get(`${API_URL}/bandeja-unificada/drafts/${initialData.draft_id}`);
          const d = r.data?.draft;
          if (d) {
            setDraftId(d.id);
            if (d.account_id) setAccountId(d.account_id);
            // Preferí chips enriquecidos si están; si no, sólo emails.
            const chipsFromEmails = (emails) => (emails || []).map(e => ({ email: e }));
            setTo(d.to_chips?.length ? d.to_chips : chipsFromEmails(d.to));
            setCc(d.cc_chips?.length ? d.cc_chips : chipsFromEmails(d.cc));
            setBcc(d.bcc_chips?.length ? d.bcc_chips : chipsFromEmails(d.bcc));
            setShowCc((d.cc || []).length > 0);
            setShowBcc((d.bcc || []).length > 0);
            setSubject(d.subject || '');
            setBody(d.body_html || '');
            if (d.grupo_key) {
              setGrupoSel({ key: d.grupo_key, label: d.grupo_label || d.grupo_key, count: d.grupo_count || 0 });
            }
            // Adjuntos: reconstruir el array liviano (id + filename + size) para render.
            if ((d.attachment_ids || []).length > 0) {
              try {
                const meta = await axios.get(
                  `${API_URL}/bandeja-unificada/attachments/meta?ids=${encodeURIComponent(d.attachment_ids.join(','))}`
                );
                setAttachments(meta.data || []);
              } catch (_) {
                // Fallback: sólo mostramos ids sin nombre
                setAttachments((d.attachment_ids || []).map(id => ({ id, filename: '(adjunto)', size: 0 })));
              }
            }
            setDraftStatus('guardado');
          }
        } catch (_) { /* silent */ }
        setHydrating(false);
        draftLoadedRef.current = true;
        return;
      }

      // ── Caso 2: Reenviar u otro prefill sin draft ──
      if (accounts.length > 0 && !accountId) setAccountId(accounts[0].id);
      if (initialData) {
        if (initialData.subject) setSubject(initialData.subject);
        if (initialData.body_html) setBody(initialData.body_html);
        if (initialData.account_id) setAccountId(initialData.account_id);
      }
      // Marcamos como listo para autosave (a la espera de que el user tipee)
      draftLoadedRef.current = true;
    };

    loadInitial();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialData?.draft_id]);

  // Cargar firma cuando cambia la cuenta
  useEffect(() => {
    if (!accountId) { setFirmaHtml(''); return; }
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/bandeja-unificada/accounts/${accountId}/firma`);
        setFirmaHtml(r.data?.firma_html || '');
      } catch (e) { setFirmaHtml(''); }
    })();
  }, [accountId]);

  // Preseleccionar la primera cuenta cuando el modal está abierto y `accounts`
  // llega después (evita drafts con account_id vacío).
  useEffect(() => {
    if (!open || accountId) return;
    if (accounts.length > 0) setAccountId(accounts[0].id);
  }, [open, accounts, accountId]);

  // Cerrar el dropdown "Añadir grupo" al hacer click afuera o presionar Escape.
  useEffect(() => {
    if (!gruposOpen) return;
    const onDocMouseDown = (e) => {
      if (gruposDropdownRef.current && !gruposDropdownRef.current.contains(e.target)) {
        setGruposOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setGruposOpen(false); };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [gruposOpen]);

  // ─── Autosave debounced ────────────────────────────────────────────────
  // Guarda un draft (colección mail_drafts) cada ~1200 ms cuando hay contenido
  // real. Sólo se activa después de que el user haya interactuado — evita
  // crear un draft basura sólo por abrir el modal (o abrir "Reenviar").
  useEffect(() => {
    if (!open) return;
    if (!draftLoadedRef.current) return;
    if (sending) return;

    // "Contenido real" = algún destinatario, asunto no vacío, cuerpo con texto
    // real, o adjuntos.
    const bodyPlain = (body || '').replace(/<[^>]+>/g, '').trim();
    const hayDestinatario = to.length + cc.length + bcc.length > 0;
    const hayContenido = hayDestinatario || subject.trim() || bodyPlain || attachments.length > 0;
    if (!userTouchedRef.current || !hayContenido) return;

    setDraftStatus('guardando');
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(async () => {
      try {
        const localId = draftId || (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now()));
        const r = await axios.put(`${API_URL}/bandeja-unificada/drafts`, {
          id: localId,
          thread_id: null,
          account_id: accountId,
          to: to.map(c => c.email),
          cc: cc.map(c => c.email),
          bcc: bcc.map(c => c.email),
          subject,
          body_html: body,
          attachment_ids: attachments.map(a => a.id),
          reply_all: false,
          to_chips: to,
          cc_chips: cc,
          bcc_chips: bcc,
          grupo_key: grupoSel?.key || null,
          grupo_label: grupoSel?.label || null,
          grupo_count: grupoSel?.count || null,
        });
        setDraftId(r.data?.draft?.id || localId);
        setDraftStatus('guardado');
      } catch (_) {
        setDraftStatus('error');
      }
    }, 1200);
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to, cc, bcc, subject, body, attachments, accountId, grupoSel]);

  // Wrap setters para marcar interacción del usuario. Usamos un callback en cada
  // handler existente (más abajo) — para simplicidad, marcamos como touched
  // en un onFocus del contenedor.
  const marcarTocado = () => { userTouchedRef.current = true; };

  const reset = useCallback(() => {
    setTo([]); setCc([]); setBcc([]); setShowCc(false); setShowBcc(false);
    setSubject(''); setBody(''); setAttachments([]);
    setError(''); setOkMsg(''); setGrupoSel(null);
    setDraftId(null); setDraftStatus(''); setHydrating(false);
    userTouchedRef.current = false;
    draftLoadedRef.current = false;
  }, []);

  const cerrar = () => { reset(); onClose?.(); };

  const descartarBorrador = async () => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    if (draftId) {
      try { await axios.delete(`${API_URL}/bandeja-unificada/drafts/${draftId}`); } catch (_) {}
    }
    cerrar();
  };

  const insertarCanned = (cr) => {
    userTouchedRef.current = true;
    setBody(b => (b || '') + cr.contenido_html);
    setCannedOpen(false);
  };

  const onFilePick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    userTouchedRef.current = true;
    setUploading(true);
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append('file', f);
        const r = await axios.post(`${API_URL}/bandeja-unificada/attachments`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setAttachments(prev => [...prev, r.data]);
      } catch (err) {
        setError(err.response?.data?.detail || `Error subiendo ${f.name}`);
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const usarGrupo = async (g) => {
    setGruposOpen(false); setError(''); setLoadingGrupo(true);
    userTouchedRef.current = true;
    try {
      const r = await axios.get(`${API_URL}/bandeja-unificada/grupos/${encodeURIComponent(g.key)}/emails`);
      const emails = r.data?.emails || [];
      const nuevos = emails.map(e => ({ email: e }));
      // Por privacidad los cargamos en BCC. Deduplicamos por email.
      setBcc(prev => {
        const emailsExistentes = new Set(prev.map(c => c.email.toLowerCase()));
        const filtrados = nuevos.filter(c => !emailsExistentes.has(c.email.toLowerCase()));
        return [...prev, ...filtrados];
      });
      setShowBcc(true);
      setGrupoSel({ key: g.key, label: g.label, count: emails.length });
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al cargar grupo');
    }
    setLoadingGrupo(false);
  };

  const enviar = async () => {
    setError(''); setOkMsg('');
    if (!accountId) { setError('Elegí la cuenta desde la que se envía'); return; }
    if (to.length === 0 && bcc.length === 0) { setError('Agregá al menos un destinatario (Para o Cco)'); return; }
    if (!subject.trim()) { setError('El asunto no puede estar vacío'); return; }
    const bodyPlain = (body || '').replace(/<[^>]+>/g, '').trim();
    if (!bodyPlain) { setError('Escribí el cuerpo del correo'); return; }

    // CRM: recolectar matriculados de los 3 campos (dedupe por trabajador_id)
    const matriculadosMap = new Map();
    [...to, ...cc, ...bcc].forEach(c => {
      if (c.trabajador_id && !matriculadosMap.has(c.trabajador_id)) {
        matriculadosMap.set(c.trabajador_id, {
          email: c.email,
          trabajador_id: c.trabajador_id,
          nombre: c.nombre || '',
          apellido: c.apellido || '',
          numero_matricula: c.numero_matricula || '',
        });
      }
    });

    setSending(true);
    try {
      const r = await axios.post(`${API_URL}/bandeja-unificada/enviar`, {
        account_id: accountId,
        to: to.map(c => c.email),
        cc: cc.map(c => c.email),
        bcc: bcc.map(c => c.email),
        subject,
        body_html: body,
        body_text: '',
        attachment_ids: attachments.map(a => a.id),
        incluir_firma: true,
        grupo_key: grupoSel?.key,
        grupo_label: grupoSel?.label,
        matriculados_vinculados: Array.from(matriculadosMap.values()),
      });
      const vinc = r.data.trabajador_id ? ' · vinculado al matriculado' : '';
      setOkMsg(`Enviado ✓ (${r.data.tipo_envio === 'masivo' ? `masivo a ${r.data.n_destinatarios} destinatarios` : `a ${r.data.n_destinatarios} destinatario/s`})${vinc}`);
      // Borrar el draft asociado (si existía) tras envío exitoso
      if (draftId) {
        try { await axios.delete(`${API_URL}/bandeja-unificada/drafts/${draftId}`); } catch (_) {}
      }
      onSent?.();
      setTimeout(() => { cerrar(); }, 1400);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al enviar');
    }
    setSending(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) cerrar(); }}>
      <DialogContent className="max-w-3xl w-[calc(100vw-1rem)] sm:w-full max-h-[95vh] sm:max-h-[92vh] overflow-y-auto p-4 sm:p-6" data-testid="compose-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <Mail className="w-5 h-5 text-purple-600" /> Redactar nuevo correo
            {hydrating && (
              <span className="text-[11px] italic text-gray-500 inline-flex items-center gap-1 font-normal" data-testid="compose-hydrating">
                <Loader2 className="w-3 h-3 animate-spin" /> Cargando borrador…
              </span>
            )}
            {!hydrating && draftStatus && (
              <span
                className={`text-[11px] italic inline-flex items-center gap-1 font-normal ${
                  draftStatus === 'guardado' ? 'text-emerald-600'
                  : draftStatus === 'error' ? 'text-red-500'
                  : 'text-gray-500'
                }`}
                data-testid="compose-draft-status"
              >
                {draftStatus === 'guardando' && <Loader2 className="w-3 h-3 animate-spin" />}
                {draftStatus === 'guardando' ? 'Guardando borrador…'
                  : draftStatus === 'guardado' ? '✓ Borrador guardado'
                  : '⚠ No se pudo guardar el borrador'}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {/* Cuenta remitente */}
          <div className="grid grid-cols-1 sm:grid-cols-[70px_1fr] gap-x-2 gap-y-2 sm:items-center">
            <Label className="text-xs text-gray-600 sm:pt-1">Desde:</Label>
            <select
              value={accountId}
              onChange={e => setAccountId(e.target.value)}
              className="border rounded px-2 py-1.5 text-sm w-full"
              data-testid="compose-account"
            >
              <option value="">— Elegí una cuenta —</option>
              {accounts.map(a => (
                <option key={a.id} value={a.id}>{a.label} · {a.email}</option>
              ))}
            </select>

            <Label className="text-xs text-gray-600 self-start pt-1">Para:</Label>
            <EmailChips value={to} onChange={(v) => { userTouchedRef.current = true; setTo(v); }} testid="compose-to" />

            {showCc && (
              <>
                <Label className="text-xs text-gray-600 self-start pt-1">Cc:</Label>
                <EmailChips value={cc} onChange={(v) => { userTouchedRef.current = true; setCc(v); }} testid="compose-cc" />
              </>
            )}
            {showBcc && (
              <>
                <Label className="text-xs text-gray-600 self-start pt-1">Cco:</Label>
                <div className="flex items-start gap-2">
                  <div className="flex-1"><EmailChips value={bcc} onChange={(v) => { userTouchedRef.current = true; setBcc(v); }} testid="compose-bcc" /></div>
                  {grupoSel && (
                    <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded inline-flex items-center gap-1 mt-1 whitespace-nowrap" data-testid="compose-grupo-chip">
                      <Users className="w-3 h-3" /> {grupoSel.label} ({grupoSel.count})
                      <button onClick={() => { setBcc([]); setGrupoSel(null); }} className="ml-1 text-red-600 hover:text-red-800" data-testid="compose-quitar-grupo">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                </div>
              </>
            )}
            <div />
            <div className="flex gap-3 -mt-1 text-xs">
              {!showCc && <button className="text-purple-700 hover:underline" onClick={() => setShowCc(true)} data-testid="compose-add-cc">+ Cc</button>}
              {!showBcc && <button className="text-purple-700 hover:underline" onClick={() => setShowBcc(true)} data-testid="compose-add-bcc">+ Cco</button>}
              <div className="relative" ref={gruposDropdownRef}>
                <button className="text-purple-700 hover:underline inline-flex items-center gap-0.5" onClick={() => setGruposOpen(o => !o)} data-testid="compose-open-grupos">
                  <Users className="w-3 h-3" /> Añadir grupo (CCO)
                </button>
                {gruposOpen && (
                  <div className="absolute z-30 mt-1 bg-white border rounded shadow max-h-64 overflow-y-auto w-72" data-testid="compose-grupos-dropdown">
                    {grupos.length === 0 ? (
                      <p className="p-3 text-xs text-gray-500 italic">No hay grupos disponibles.</p>
                    ) : grupos.map(g => (
                      <button
                        key={g.key}
                        onClick={() => usarGrupo(g)}
                        className="w-full text-left px-3 py-2 hover:bg-purple-50 border-b last:border-b-0"
                        data-testid={`compose-grupo-opt-${g.key}`}
                      >
                        <p className="text-sm font-medium">{g.label}</p>
                        <p className="text-xs text-gray-500">{g.count} destinatarios</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {loadingGrupo && <Loader2 className="w-3 h-3 animate-spin text-purple-600" />}
            </div>

            <Label className="text-xs text-gray-600">Asunto:</Label>
            <Input value={subject} onChange={e => { userTouchedRef.current = true; setSubject(e.target.value); }} className="h-9" data-testid="compose-subject" />
          </div>

          {/* Editor */}
          <RichTextEditor value={body} onChange={(v) => { userTouchedRef.current = true; setBody(v); }} placeholder="Escribí el mensaje…" autoFocus minHeight={200} />

          {/* Firma preview */}
          {firmaHtml && (
            <details className="text-xs text-gray-600 border rounded bg-white p-2">
              <summary className="cursor-pointer">Firma que se adjuntará automáticamente</summary>
              <div className="mt-2 prose prose-sm max-w-none border-t pt-2" dangerouslySetInnerHTML={{ __html: firmaHtml }} />
            </details>
          )}

          {/* Adjuntos */}
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="compose-adjuntos">
              {attachments.map(a => (
                <div key={a.id} className="inline-flex items-center gap-1.5 bg-white border rounded px-2 py-1 text-xs">
                  <FileText className="w-3.5 h-3.5 text-purple-600" />
                  <span className="max-w-[180px] truncate">{a.filename}</span>
                  <span className="text-gray-500">{Math.round(a.size / 1024)} KB</span>
                  <button onClick={() => setAttachments(prev => prev.filter(x => x.id !== a.id))} className="text-red-500 hover:text-red-700">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {error && <p className="text-sm text-red-600" data-testid="compose-error">{error}</p>}
          {okMsg && <p className="text-sm text-emerald-600" data-testid="compose-ok">{okMsg}</p>}

          {/* Barra de acciones */}
          <div className="flex items-center gap-2 flex-wrap border-t pt-3">
            <input ref={fileInputRef} type="file" multiple hidden onChange={onFilePick} data-testid="compose-file-input" />
            <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="compose-adjuntar">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
              <span className="ml-1 text-xs">Adjuntar</span>
            </Button>
            <div className="relative">
              <Button size="sm" variant="outline" onClick={() => setCannedOpen(o => !o)} data-testid="compose-open-canned">
                <Sparkles className="w-4 h-4" /><span className="ml-1 text-xs">Respuesta frecuente</span><ChevronDown className="w-3 h-3 ml-1" />
              </Button>
              {cannedOpen && (
                <div className="absolute z-20 mt-1 bg-white border rounded-lg shadow-lg w-80 max-h-64 overflow-y-auto" data-testid="compose-canned-dropdown">
                  {canned.length === 0 ? (
                    <p className="p-3 text-xs text-gray-500 italic">Sin respuestas frecuentes.</p>
                  ) : canned.map(c => (
                    <button key={c.id} onClick={() => insertarCanned(c)} className="w-full text-left px-3 py-2 hover:bg-purple-50 border-b last:border-b-0">
                      <p className="font-medium text-sm truncate">{c.titulo}</p>
                      {c.categoria && <p className="text-xs text-gray-500">{c.categoria}</p>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex-1" />
            {draftId && (
              <Button size="sm" variant="ghost" onClick={descartarBorrador} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="compose-descartar-borrador" title="Elimina el borrador guardado y cierra">
                <Trash2 className="w-4 h-4" />
                <span className="ml-1 text-xs hidden sm:inline">Descartar borrador</span>
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={cerrar} data-testid="compose-cancel">Cancelar</Button>
            <Button size="sm" onClick={enviar} disabled={sending || !accountId} className="bg-purple-600 hover:bg-purple-700" data-testid="compose-send">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              <span className="ml-1">Enviar</span>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Input de destinatarios con:
 * - Chips (verdes cuando son matriculados con `trabajador_id`, morados si email libre).
 * - Autocomplete de matriculados a medida que se escribe (>=2 chars, 250ms debounce).
 * - Busca por Apellido, Nombre, Matrícula o DNI (endpoint `/matriculados/buscar`).
 * - Enter/coma/punto y coma/espacio confirman email libre si tipeaste un `@`.
 * value/onChange operan sobre objetos: { email, trabajador_id?, nombre?, apellido?, numero_matricula? }
 */
function EmailChips({ value, onChange, testid = 'chips' }) {
  const [text, setText] = useState('');
  const [opts, setOpts] = useState([]);
  const [openMenu, setOpenMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const debounceRef = useRef(null);

  // Autocomplete
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!text || text.length < 2) { setOpts([]); setOpenMenu(false); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API_URL}/bandeja-unificada/matriculados/buscar?q=${encodeURIComponent(text)}`);
        const arr = (r.data || []).filter(m => m.email);
        setOpts(arr);
        setOpenMenu(arr.length > 0);
        setActiveIdx(0);
      } catch (e) { setOpts([]); setOpenMenu(false); }
      setLoading(false);
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [text]);

  const addFromMatriculado = (m) => {
    if (!m?.email) return;
    // Evitar duplicados por email
    if (value.some(c => c.email.toLowerCase() === m.email.toLowerCase())) {
      setText(''); setOpts([]); setOpenMenu(false); return;
    }
    onChange([...value, {
      email: m.email,
      trabajador_id: m.id,
      nombre: m.nombre || '',
      apellido: m.apellido || '',
      numero_matricula: m.numero_matricula || '',
    }]);
    setText(''); setOpts([]); setOpenMenu(false);
  };

  const commitFreeText = (raw) => {
    const partes = (raw || '').split(/[,\s;]+/).map(s => s.trim()).filter(s => s && s.includes('@'));
    if (partes.length === 0) return;
    const existentes = new Set(value.map(c => c.email.toLowerCase()));
    const nuevos = partes
      .filter(e => !existentes.has(e.toLowerCase()))
      .map(email => ({ email }));
    if (nuevos.length > 0) onChange([...value, ...nuevos]);
    setText('');
  };

  const onKey = (e) => {
    if (openMenu && opts.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(opts.length - 1, i + 1)); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx(i => Math.max(0, i - 1)); return; }
      if (e.key === 'Enter')     { e.preventDefault(); addFromMatriculado(opts[activeIdx]); return; }
      if (e.key === 'Escape')    { setOpenMenu(false); return; }
    }
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      if (text.includes('@')) { e.preventDefault(); commitFreeText(text); }
    } else if (e.key === 'Backspace' && !text && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  const onBlur = () => {
    // Dar tiempo a que el click del menú se dispare antes de cerrar.
    setTimeout(() => setOpenMenu(false), 180);
    if (text.includes('@')) commitFreeText(text);
  };

  return (
    <div className="relative">
      <div className="border rounded px-1.5 py-1 flex flex-wrap gap-1 min-h-9 bg-white" data-testid={testid}>
        {value.map((c, i) => {
          const esMat = Boolean(c.trabajador_id);
          const nombreDisp = c.apellido ? `${c.apellido}, ${c.nombre}` : c.email;
          return (
            <span
              key={`${c.email}-${i}`}
              className={`inline-flex items-center gap-1 text-xs rounded px-1.5 py-0.5 ${esMat ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' : 'bg-purple-100 text-purple-800'}`}
              title={esMat ? `Matriculado · ${c.email} · Mat. ${c.numero_matricula || '—'}` : c.email}
              data-testid={`${testid}-chip-${i}`}
            >
              {esMat && <UserCircle className="w-3 h-3" />}
              {esMat ? (
                <>
                  <span className="font-medium">{nombreDisp}</span>
                  <span className="opacity-60">·</span>
                  <span className="opacity-70">{c.email}</span>
                </>
              ) : c.email}
              <button onClick={() => onChange(value.filter((_, j) => j !== i))} className="hover:text-red-600" data-testid={`${testid}-chip-remove-${i}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          );
        })}
        <input
          className="flex-1 min-w-[140px] text-sm outline-none px-1"
          placeholder={value.length === 0 ? 'Escribí apellido, matrícula o email…' : ''}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={onKey}
          onFocus={() => { if (opts.length > 0) setOpenMenu(true); }}
          onBlur={onBlur}
          data-testid={`${testid}-input`}
        />
        {loading && <Loader2 className="w-3 h-3 animate-spin text-purple-600 self-center" />}
      </div>

      {/* Dropdown de matriculados */}
      {openMenu && opts.length > 0 && (
        <div className="absolute z-40 left-0 right-0 mt-1 bg-white border rounded shadow-lg max-h-64 overflow-y-auto" data-testid={`${testid}-menu`}>
          {opts.map((m, idx) => (
            <button
              key={m.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => addFromMatriculado(m)}
              className={`w-full text-left px-3 py-1.5 border-b last:border-b-0 ${idx === activeIdx ? 'bg-emerald-50' : 'hover:bg-emerald-50/60'}`}
              data-testid={`${testid}-opt-${m.id}`}
            >
              <p className="text-sm font-medium flex items-center gap-1">
                <UserCircle className="w-3.5 h-3.5 text-emerald-700" />
                {m.apellido}, {m.nombre}
              </p>
              <p className="text-xs text-gray-500 ml-4">
                <IdCard className="w-3 h-3 inline mr-0.5" /> Mat. {m.numero_matricula || '—'} · DNI {m.documento || '—'} · {m.email || '(sin email)'}
              </p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default ComposeDialog;
